<script lang="ts" setup>
import type { RefresherCustomEvent } from '@ionic/vue';
import type { MaterialLocationActionModal } from '#build/components';
import { useTemplateRef } from 'vue';

const { usingTouch } = useInputTracker();

interface Props {
  selectedLocationId: string;
}
const props = defineProps<Props>();
const { t } = useEkI18n();
const hideOutOfStock = ref<boolean>(false);
const visibleItemsCount = ref<number>(10);
const searchQuery = ref<string>('');
const locationItemByLocationIdStore = useLocationItemByLocationIdStore();

const locationActionModal = useTemplateRef('locationActionModal');
const materialLocationItemListMaterial = useTemplateRef('materialLocationItemListMaterial');
const request: LocationItemForLocationIdsRequest = {
  LocationIds: [props.selectedLocationId],
  PageSize: visibleItemsCount.value,
  PageNumber: 1,
  SearchQuery: searchQuery.value,
  HideOutOfStock: hideOutOfStock.value
};
await locationItemByLocationIdStore.setLocationItemByLocationIdLocationPage(request);
const id = useId();
const isScan = ref(false);
await hideLoading();

async function handleRefresh(event: RefresherCustomEvent) {
  await resetLocationItems();
  event.target.complete();
}

function handleEnterPress(event: KeyboardEvent) {
  isScan.value = false;
  const newQuery = (event.target as HTMLInputElement).value;
  searchQuery.value = newQuery;
}

function handleScanWithTimeout() {
  if (isScan.value) {
    setTimeout(() => {
      handleScan();
    }, 500);
  }
}

async function handleScan() {
  isScan.value = true;
  const result = await startScan();
  
  if (result) {
    searchQuery.value = result;
    request.SearchQuery = result;

    await locationItemByLocationIdStore.setLocationItemByLocationIdLocationPage(request);

    if (locationItemByLocationIdStore._getLocationItemByLocationIdLocationPage?.length === 1) {
      openMainModal(locationItemByLocationIdStore._getLocationItemByLocationIdLocationPage[0]);
    }
  }
}

function openMainModal(item: MaterialItemApp) {
  locationActionModal.value?.openModal(item, true);
}

async function resetLocationItems() {
  materialLocationItemListMaterial.value?.hideOutOfStockChanged(hideOutOfStock.value);
  await materialLocationItemListMaterial.value?.resetItemsLocationPage();
}

function resetSearch() {
  searchQuery.value = '';
}
</script>

<template>
  <IonToolbar>
    <IonSearchbar
      :value="searchQuery"
      inputmode="search"
      :placeholder="t('search.placeholder')"
      :debounce="300"
      @keyup.enter="handleEnterPress"
    />
    <IonButton
      slot="end"
      color="dark"
      fill="clear"
      @click="handleScan"
    >
      <FontAwesomeIcon
        slot="end"
        style="font-size: 1rem;"
        :icon="['fal', 'barcode-read']"
      />
    </IonButton>
    <IonButton
      :id="id"
      slot="end"
      fill="clear"
      color="dark"
    >
      <FontAwesomeIcon
        style="font-size: 1rem;"
        :icon="['fal', 'filter']"
      />
    </IonButton>
    <IonPopover
      :trigger="id"
      trigger-action="click"
    >
      <IonItem lines="none">
        <IonCheckbox
          v-model="hideOutOfStock"
          aria-label="HideLocationItems"
          @ion-change="resetLocationItems()"
        >
          {{ t('materials.checkbox.stockitems') }}
        </IonCheckbox>
      </IonItem>
    </IonPopover>
  </IonToolbar>
  <IonContent>
    <IonRefresher
      slot="fixed"
      :disabled="!usingTouch"
      @ion-refresh="handleRefresh($event)"
    >
      <IonRefresherContent />
    </IonRefresher>
    <MaterialLocationItemListMaterial
      v-show="(locationItemByLocationIdStore._getLocationItemByLocationIdLocationPage ?? []).length > 0"
      ref="materialLocationItemListMaterial"
      :location-ids="[props.selectedLocationId,]"
      :search-query="searchQuery"
      item-type="locations"
      page="locations"
      :visible-items-count="visibleItemsCount"
      :hide-out-of-stock="hideOutOfStock"
      @open-location-modal="openMainModal"
    />
    <UserMessagesSearch
      v-if="(locationItemByLocationIdStore._getLocationItemByLocationIdLocationPage ?? []).length === 0"
      :header="t('search.noResults', {item: t('materials.item', 2).toLowerCase()})"
      icon="fa-fw fa-light fa-clipboard"
      :subheader="t('search.noResultsText')"
    />
    <MaterialLocationActionModal
      ref="locationActionModal"
      page="locations"
      :locations-ids="[props.selectedLocationId]"
      @location-item-by-location-id="resetSearch"
      @scan-again="handleScanWithTimeout"
    />
  </IonContent>
</template>

<style scoped>

.scan-box{
  border: 2px solid #fff;
  box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.5);
  content: '';
  display: block;
  left: 50%;
  height: 300px;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
}
ion-popover {
    --width: auto;
}
</style>
